import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  Tooltip,
  useTheme
} from '@mui/material';
import { Settings } from '@mui/icons-material';
import { Agent } from '../../types/agent';
import ZAPIConfigModal from './ZAPIConfigModal';

interface ZAPIConnectionModalProps {
  open: boolean;
  onClose: () => void;
  agent: Agent | null;
  onConnectionSuccess: () => void;
  onConfigUpdate?: (config: { clientToken: string; instanceId: string; instanceToken: string }) => void;
}

const ZAPIConnectionModal: React.FC<ZAPIConnectionModalProps> = ({
  open,
  onClose,
  agent,
  onConnectionSuccess,
  onConfigUpdate
}) => {
  const theme = useTheme();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isRequestingCode, setIsRequestingCode] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [isSavingConfig, setIsSavingConfig] = useState(false);

  useEffect(() => {
    if (open) {
      setPhoneNumber('');
      setVerificationCode('');
      setError('');
      setSuccess('');
    }
  }, [open]);

  const formatPhoneNumber = (phone: string) => {
    // Remove todos os caracteres não numéricos
    const cleaned = phone.replace(/\D/g, '');
    
    // Se não começar com 55, adiciona
    if (!cleaned.startsWith('55')) {
      return '55' + cleaned;
    }
    
    return cleaned;
  };

  const handleRequestCode = async () => {
    if (!agent?.zApiConfig || !phoneNumber) {
      setError('Configurações Z-API não encontradas ou número de telefone não informado');
      return;
    }

    setIsRequestingCode(true);
    setError('');
    setSuccess('');

    try {
      const formattedPhone = formatPhoneNumber(phoneNumber);
      
      const response = await fetch(
        `https://api.z-api.io/instances/${agent.zApiConfig.instanceId}/token/${agent.zApiConfig.instanceToken}/phone-code/${formattedPhone}`,
        {
          method: 'GET',
          headers: {
            'Client-Token': agent.zApiConfig.clientToken
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Formatar código com hífen (ex: GBWDPBNH -> GBWD-PBNH)
        const formattedCode = data.code.length === 8 
          ? `${data.code.substring(0, 4)}-${data.code.substring(4)}`
          : data.code;
        setSuccess(`Código de verificação: ${formattedCode}`);
        setVerificationCode(data.code);
        startStatusCheck();
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Erro ao solicitar código de verificação');
      }
    } catch (err) {
      setError('Erro ao solicitar código de verificação');
    } finally {
      setIsRequestingCode(false);
    }
  };

  const startStatusCheck = () => {
    setIsCheckingStatus(true);
    const interval = setInterval(async () => {
      try {
        const response = await fetch(
          `https://api.z-api.io/instances/${agent?.zApiConfig?.instanceId}/token/${agent?.zApiConfig?.instanceToken}/status`,
          {
            method: 'GET',
            headers: {
              'Client-Token': agent?.zApiConfig?.clientToken || ''
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.connected) {
            clearInterval(interval);
            setIsCheckingStatus(false);
            onConnectionSuccess();
          }
        }
      } catch (err) {
        console.error('Erro ao verificar status:', err);
      }
    }, 3000); // Verifica a cada 3 segundos

    // Limpa o interval após 5 minutos
    setTimeout(() => {
      clearInterval(interval);
      setIsCheckingStatus(false);
    }, 300000);
  };

  const handleRetryCode = () => {
    setSuccess('');
    setError('');
    setVerificationCode('');
    handleRequestCode();
  };

  const handleConfigSave = async (config: { clientToken: string; instanceId: string; instanceToken: string }) => {
    if (!onConfigUpdate) return;
    
    setIsSavingConfig(true);
    try {
      await onConfigUpdate(config);
      setConfigModalOpen(false);
    } catch (error) {
      console.error('Erro ao salvar configuração:', error);
    } finally {
      setIsSavingConfig(false);
    }
  };

  const isPhoneValid = phoneNumber.replace(/\D/g, '').length >= 10;
  

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Conectar Z-API
        </Typography>
        <Tooltip title="Editar Credenciais">
          <IconButton 
            onClick={() => setConfigModalOpen(true)}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <Settings />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Digite o número do telefone que receberá o código de verificação.
          </Typography>
          
          
          <TextField
            label="Número do Telefone"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            fullWidth
            placeholder="(11) 99999-9999"
            InputProps={{
              startAdornment: <InputAdornment position="start">+55</InputAdornment>
            }}
            helperText="Digite o número com DDD (ex: 11999999999)"
          />

          {success && (
            <Alert severity="success">
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                Código de verificação recebido!
              </Typography>
              <Box 
                sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  p: 3,
                  backgroundColor: '#f8f9fa',
                  borderRadius: 2,
                  border: '2px solid #e9ecef',
                  mb: 2
                }}
              >
                <Typography variant="body2" sx={{ mb: 1, color: '#6c757d' }}>
                  Digite este código no seu WhatsApp:
                </Typography>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 700, 
                    letterSpacing: '0.2em',
                    color: '#28a745',
                    fontFamily: 'monospace',
                    textAlign: 'center',
                    backgroundColor: 'white',
                    padding: '12px 24px',
                    borderRadius: 1,
                    border: '2px solid #28a745',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                >
                  {success.replace('Código de verificação: ', '')}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Adicione este código na conexão do WhatsApp no seu aparelho.
                Vá em Configurações → WhatsApp → Conectar e digite o código.
              </Typography>
            </Alert>
          )}

          {error && (
            <Alert severity="error">
              {error}
            </Alert>
          )}

          {isCheckingStatus && (
            <Alert severity="info">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2">
                  Aguardando conexão... Verificando status automaticamente.
                </Typography>
              </Box>
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={onClose} color="inherit">
          Cancelar
        </Button>
        {!success ? (
          <Button
            variant="contained"
            sx={{ color: theme.palette.mode === 'dark' ? "#000" : undefined }}
            onClick={handleRequestCode}
            disabled={!isPhoneValid || isRequestingCode}
          >
            {isRequestingCode ? <CircularProgress size={20} /> : 'Solicitar Código'}
          </Button>
        ) : (
          <Button
            variant="outlined"
            onClick={handleRetryCode}
            disabled={isRequestingCode}
          >
            {isRequestingCode ? <CircularProgress size={20} /> : 'Tentar Novamente'}
          </Button>
        )}
      </DialogActions>

      <ZAPIConfigModal
        open={configModalOpen}
        onClose={() => setConfigModalOpen(false)}
        agent={agent}
        onSave={handleConfigSave}
        isLoading={isSavingConfig}
      />
    </Dialog>
  );
};

export default ZAPIConnectionModal;
